<template>
    <div
        ref="el"
        v-click-outside="handleClickOutside"
        class="search"
    >
        <div class="input-container">
            <input
                ref="inputRef"
                v-model="search"
                type="text"
                placeholder="Search by Operation Name, Alberta Township System (ATS), Plan Block Lot (PBL) or City"
                @input="handleInputChange"
                @keydown.down="onArrowDown"
                @keydown.up="onArrowUp"
                @keydown.enter="onEnter"
                @focus="onFocus"
            >
            <span
                v-if="search.length"
                class="mdi mdi-close cursor-pointer"
                @click="clearForm"
            />
            <span
                v-if="isSearching"
                class="mdi mdi-loading animate-spin"
                @click="clearForm"
            />
            <span
                v-if="search.length === 0"
                class="mdi mdi-magnify"
            />
        </div>
        <div
            v-show="isFocused"
            class="search-float"
        >
            <ul
                v-show="search.length"
                class="autocomplete-results"
            >
                <li
                    class="result-item font-bold italic"
                    :class="{ 'is-active': selectedSuggestionIndex === -1 }"
                    @click="onEnter"
                >
                    <span>{{ search }}</span>
                    <span class="type">Search</span>
                </li>
                <li
                    v-if="searchSuggestions.length === 0"
                    class="result-item italic text-sm"
                >
                    <span>No Suggestions</span>
                </li>
                <li
                    v-for="(result, i) in searchSuggestions"
                    :key="i"
                    class="result-item"
                    :class="{ 'is-active': i === selectedSuggestionIndex }"
                    @click="selectResult(result)"
                >
                    <span>
                        <img
                            v-if="result.type === 'Municipality'"
                            :src="CityIcon"
                            class="inline mr-1.5"
                        >
                        <span v-html="highlighSearchResult(result.title)" />
                    </span>

                    <span class="type">{{ result.type }}</span>
                </li>
            </ul>
            <div
                v-show="isFocused"
                class="search-help-container"
            >
                <div
                    v-for="({ type, title, example, withIcon }) in searchTypes"
                    :key="type"
                    :class="{ 'search-type': true, 'is-active': selectedSearchType === type }"
                >
                    <div
                        class="info"
                        @click="selectSearchType(type)"
                    >
                        <span class="title">{{ title }}</span>
                        <span class="example">Examples: {{ example }}</span>
                        <img
                            v-if="withIcon"
                            src="@/assets/img/info-icon.svg"
                            class="w-8 h-8"
                        >
                    </div>
                    <ATSPanel
                        v-if="type === 'ats' && selectedSearchType === type"
                        :handle-search="atsPblSearch"
                    />
                    <PBLPanel
                        v-else-if="type === 'pbl' && selectedSearchType === type"
                        :handle-search="atsPblSearch"
                    />
                </div>
            </div>
        </div>
        <div
            v-if="state.filteredStatusCode || state.currentSearchQuery"
            class="search-criteria"
        >
            <span>Searching for</span>
            <div
                v-if="state.filteredStatusCode"
                class="search-pill"
            >
                <span class="search-desc">Status: {{ state.filteredStatusDescription }}</span>
                <span
                    class="mdi mdi-close cursor-pointer"
                    @click="toggleFilterStatus()"
                />
            </div>
            <div
                v-if="state.currentSearchQuery"
                class="search-pill"
            >
                <span class="search-desc">Text: "{{ state.currentSearchQuery }}"</span>
                <span
                    class="mdi mdi-close cursor-pointer"
                    @click="clearForm"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import _ from 'lodash';

import useApp, { SearchType, SuggestionResult } from '@/composables';
import CityIcon from '@/assets/img/city_icon.svg';

import ATSPanel from './ATSPanel.vue';
import PBLPanel from './PBLPanel.vue';

const SEARCH_DELAY_TIME = 300;

export default defineComponent({
    name: 'Search',
    components: {
        ATSPanel,
        PBLPanel
    },
    setup() {
        const { state, clearOpenSite, getSearchSuggestions, doSearch, clearSearch, loadSuggestionResult, toggleFilterStatus } = useApp();

        const MAX_RESULTS = 10;
        const el = ref(null);
        const search = ref('');
        const selectedSuggestionIndex = ref<number>(-1);
        const isSearching = ref<boolean>(false);
        const isFocused = ref<boolean>(false);
        const selectedSearchType = ref<SearchType>('');
        const searchSuggestions = ref<SuggestionResult[]>([]);
        const inputRef = ref<HTMLInputElement>();

        const searchTypes = [
            { type: 'ats', title: 'Alberta Township System (ATS) Format', example: 'W4-27-38-11-NW, W4-3-40-3-SE-2', withIcon: true },
            { type: 'pbl', title: 'Plan, Block, Lot (PBL) Format', example: '421429-1-2, 5826CL-8-3', withIcon: true },
        ];

        const selectSearchType = (type: SearchType) => {
            if (selectedSearchType.value === type) {
                selectedSearchType.value = '';
            } else {
                selectedSearchType.value = type;
            }
        };

        const clearForm = () => {
            search.value = '';
            searchSuggestions.value = [];
            clearSearch();
        };

        const selectResult = (result: SuggestionResult) => {
            if (result.type === 'Municipality' || result.type === 'Neighborhood') {
                doSearch(result.title, result.type);
                selectedSuggestionIndex.value = -1;
                isFocused.value = false;
                searchSuggestions.value = [];
                search.value = result.title;
            } else if (result.item) {
                selectedSuggestionIndex.value = -1;
                isFocused.value = false;
                searchSuggestions.value = [];
                search.value = result.title;
                loadSuggestionResult(result);
            }
        };

        const handleClickOutside = (e: MouseEvent) => {
            e.stopPropagation();
            if ((e.target as any).type === 'text') {
                return;
            }
            selectedSuggestionIndex.value = -1;
            isFocused.value = false;
        };

        const onArrowDown = () => {
            if (selectedSuggestionIndex.value < searchSuggestions.value.length) {
                selectedSuggestionIndex.value += 1;
            }
        };

        const onArrowUp = () => {
            if (selectedSuggestionIndex.value > 0) {
                selectedSuggestionIndex.value -= 1;
            } else {
                selectedSuggestionIndex.value = -1;
            }
        };

        const onEnter = () => {
            isFocused.value = false;
            inputRef.value?.blur();
            clearOpenSite();

            // when pressing enter on a suggestion item
            if (selectedSuggestionIndex.value !== -1 && !isSearching.value) {
                loadSuggestionResult(searchSuggestions.value[selectedSuggestionIndex.value]);
            } else {
                isSearching.value = true;
                doSearch(search.value, selectedSearchType.value);
                isSearching.value = false;
            }
        };

        const onFocus = () => {
            isFocused.value = true;
        };

        const highlighSearchResult = (title: string) => {
            const startIndex = title.toLowerCase().indexOf(search.value.toLowerCase());
            const exactOccurence = title.slice(startIndex, startIndex + search.value.length);

            return title.replace(exactOccurence, `<span class="highlightResults">${exactOccurence}</span>`);
        };

        const handleInputChange = _.debounce((event: InputEvent) => {
            if (search.value.length === 0) {
                clearForm();
                return;
            }

            if (!isFocused.value) {
                isFocused.value = true;
            }

            isSearching.value = true;
            selectedSearchType.value = '';
            searchSuggestions.value = getSearchSuggestions(search.value, selectedSearchType.value, MAX_RESULTS);
            setTimeout(() => {
                isSearching.value = false;
            }, 300);
        }, SEARCH_DELAY_TIME);

        const atsPblSearch = (query: string) => {
            search.value = query;
            onEnter();
            selectedSearchType.value = '';
        };

        return {
            el,
            searchTypes,
            selectedSearchType,
            search,
            isSearching,
            isFocused,
            selectedSuggestionIndex,
            searchSuggestions,
            inputRef,
            state,
            // funcs
            selectSearchType,
            handleClickOutside,
            onArrowDown,
            onArrowUp,
            onEnter,
            onFocus,
            clearForm,
            highlighSearchResult,
            handleInputChange,
            selectResult,
            atsPblSearch,
            toggleFilterStatus,
            CityIcon
        };
    }
});
</script>

<style lang="postcss" scoped>
.search {
    @apply flex flex-col relative;
    .search-criteria {
        @apply text-sm flex mt-2;
        .search-pill {
            @apply mx-2 bg-blue-400 rounded pl-3 pr-1 text-white text-sm;

            .mdi-close {
                @apply ml-3 ;

                &:hover {
                    @apply rounded bg-gray-200 text-gray-600;
                }
            }
        }
    }

    .input-container {
        @apply flex border py-1 px-2;;
        input {
            @apply w-full border-0 text-sm outline-none block;
        }

        .mdi {
            @apply text-xl;
            color: #878787;
        }
    }

    .search-float {
        @apply absolute p-0 m-0 w-full z-10;
        top: 42px;
    }

    .autocomplete-results,
    .search-help-container {
        @apply flex flex-col bg-white;
        border: 1px solid #eeeeee;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2), 0 3px 10px 0 rgba(0, 0, 0, 0.19);

    }

    .autocomplete-results {
        @apply mb-1;

        .result-item {
            @apply list-none text-left cursor-pointer flex flex-row justify-between px-2 py-1;

            :deep(span.highlightResults) {
                @apply text-blue-400;
            }

            &:hover, &.is-active {
                @apply bg-blue-400 text-white;

                :deep(span.highlightResults) {
                    @apply text-black;
                }

                span:nth-child(2) {
                    @apply text-gray-200;
                }
            }

            &:hover {
                @apply bg-opacity-50;
            }

            span:nth-child(2) {
                @apply text-gray-500;
            }
        }
    }

    .search-help-container {
        .search-type {
            @apply list-none text-left cursor-pointer px-2 py-1;

            &:hover:not(.is-active) {
                @apply bg-blue-400 text-white;

                :deep(span.highlightResults) {
                    @apply text-black;
                }

                :deep(span.example) {
                    @apply text-gray-200;
                }
            }

            .info {
                @apply flex flex-row flex-wrap items-center;

                .title {
                    @apply w-1/2;
                }
                .example {
                    @apply text-gray-500 text-xs flex-grow;
                }
                .mdi {
                    @apply float-right;
                }
            }
        }
    }
}
</style>
